import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { MonitorChannel } from './MonitorChannel';
import type { ScreenRole } from './types';
import { HEARTBEAT_INTERVAL_MS } from './types';
import { getViewer, getDefaultViewer } from '../viewer/viewerRegistry';

/**
 * Standalone viewer page — opened in child windows by MonitorManager.
 *
 * Reads study context from URL query params and renders a full-bleed
 * viewer iframe with no worklist, tabs, or header chrome. The viewer
 * type is determined by the `viewer` query param and resolved through
 * the viewer registry, making this component viewer-agnostic.
 *
 * Query params:
 *  - studyUID   : StudyInstanceUID to load
 *  - site       : Site ID
 *  - role       : ScreenRole for this window
 *  - windowId   : Unique identifier assigned by MonitorManager
 *  - viewer     : Viewer provider ID (e.g. 'ohif', 'gwt', 'ohif-v312')
 */
export function StandaloneViewer() {
  const [searchParams] = useSearchParams();
  const studyUID = searchParams.get('studyUID') ?? '';
  const siteId = searchParams.get('site') ?? '';
  const role = (searchParams.get('role') ?? 'secondary-viewer') as ScreenRole;
  const windowId = searchParams.get('windowId') ?? 'standalone';
  const viewerId = searchParams.get('viewer') ?? '';
  const objKey = searchParams.get('objKey') ?? studyUID;

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [ohifReady, setOhifReady] = useState(false);
  const ohifReadyRef = useRef(false);
  const pendingProtocolRef = useRef<unknown>(null);
  const channelRef = useRef<MonitorChannel | null>(null);

  // Resolve the viewer provider from the registry
  const viewerProvider = (viewerId ? getViewer(viewerId) : undefined) ?? getDefaultViewer();

  // Build the viewer URL through the provider (viewer-agnostic)
  const sessionId = localStorage.getItem('brit_sid') ?? '';
  const viewerConfig = viewerProvider.buildViewerConfig({
    study: { uid: studyUID, objKey } as Parameters<typeof viewerProvider.buildViewerConfig>[0]['study'],
    siteId,
    sessionId,
  });
  const viewerUrl = viewerConfig.url ?? '';

  // Create the sync adapter from the viewer provider
  useEffect(() => {
    if (!channelRef.current?.isOpen || !viewerProvider.createSyncAdapter) return;

    const adapter = viewerProvider.createSyncAdapter(
      channelRef.current,
      windowId,
      () => iframeRef.current,
    );
    if (!adapter) return;

    adapter.start();
    return () => adapter.stop();
  }, [viewerProvider, windowId]);

  // Request fullscreen on mount — the parent window's click gesture
  // propagates to us, so this should succeed if the browser / permission
  // policy allows it.  If it fails, the window is already sized to fill
  // the target screen so the experience is nearly identical.
  useEffect(() => {
    if (document.fullscreenElement) return; // already fullscreen
    document.documentElement.requestFullscreen?.()?.catch(() => {});
  }, []);

  // Listen for BRIT_OHIF_READY from the OHIF iframe — this fires after
  // OHIF's React app and postMessage bridge are fully initialized, which
  // is later than the iframe's onLoad event.
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'BRIT_OHIF_READY') {
        ohifReadyRef.current = true;
        setOhifReady(true);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  // Apply pending protocol once OHIF is ready (Phase 3)
  useEffect(() => {
    if (!ohifReady) return;

    const applyToIframe = (protocol: unknown) => {
      const iframe = iframeRef.current;
      if (iframe?.contentWindow) {
        iframe.contentWindow.postMessage(
          { type: 'BRIT_APPLY_PROTOCOL', protocol },
          '*',
        );
      }
    };

    // Check ref (protocol arrived via BroadcastChannel before OHIF was ready)
    if (pendingProtocolRef.current) {
      const protocol = pendingProtocolRef.current;
      pendingProtocolRef.current = null;
      applyToIframe(protocol);
    }

    // Check sessionStorage (after a study-change reload)
    const key = `brit-pending-protocol-${windowId}`;
    const stored = sessionStorage.getItem(key);
    if (stored) {
      sessionStorage.removeItem(key);
      try {
        applyToIframe(JSON.parse(stored));
      } catch {
        // Invalid stored protocol — ignore
      }
    }
  }, [ohifReady, windowId]);

  // Open the BroadcastChannel and send heartbeats
  useEffect(() => {
    const channel = new MonitorChannel();
    channel.open();
    channelRef.current = channel;

    // Announce ourselves
    channel.send({
      type: 'WINDOW_OPENED',
      windowId,
      role,
      screenLabel: document.title,
    });

    // Heartbeat so the primary window knows we're alive
    const heartbeat = setInterval(() => {
      channel.send({ type: 'HEARTBEAT', windowId, role });
    }, HEARTBEAT_INTERVAL_MS);

    // Listen for messages from the primary window
    const unsubscribe = channel.subscribe((msg) => {
      if (msg.type === 'LOAD_STUDY' && msg.targetWindowId === windowId) {
        // Reload with new study — simplest approach is to update location
        const params = new URLSearchParams({
          studyUID: msg.studyInstanceUID,
          site: msg.siteId,
          role,
          windowId,
        });
        window.location.replace(`/viewer-standalone?${params.toString()}`);
      }

      if (msg.type === 'LOAD_STUDY_WITH_PROTOCOL' && msg.targetWindowId === windowId) {
        // Phase 3: Apply a hanging protocol to this window's OHIF iframe.
        // If the study changed, reload with the new study. If it's the same
        // study, apply the protocol in-place via postMessage.
        const iframe = iframeRef.current;
        const currentStudy = new URLSearchParams(window.location.search).get('studyUID');

        if (msg.studyUID !== currentStudy) {
          // Different study — need to reload the viewer with the new study,
          // then apply the protocol once OHIF signals ready.
          const params = new URLSearchParams({
            studyUID: msg.studyUID,
            site: siteId,
            role,
            windowId,
            viewer: viewerId,
          });
          // Store the protocol so we can apply it after reload
          sessionStorage.setItem(
            `brit-pending-protocol-${windowId}`,
            JSON.stringify(msg.ohifProtocol),
          );
          window.location.replace(`/viewer-standalone?${params.toString()}`);
        } else if (ohifReadyRef.current && iframe?.contentWindow) {
          // Same study, OHIF ready — apply protocol in-place via postMessage
          iframe.contentWindow.postMessage(
            { type: 'BRIT_APPLY_PROTOCOL', protocol: msg.ohifProtocol },
            '*',
          );
        } else {
          // Same study but OHIF not ready yet — buffer for when it's ready
          pendingProtocolRef.current = msg.ohifProtocol;
        }
      }
    });

    return () => {
      clearInterval(heartbeat);
      channel.send({ type: 'WINDOW_CLOSING', windowId });
      unsubscribe();
      channel.close();
    };
  }, [windowId, role, siteId, viewerId]);

  // Announce closing via beforeunload
  useEffect(() => {
    const handleUnload = () => {
      channelRef.current?.send({ type: 'WINDOW_CLOSING', windowId });
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [windowId]);

  // Set window title
  useEffect(() => {
    document.title = `BRIT PACS Viewer — ${role}`;
  }, [role]);

  if (!studyUID) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#1a1a2e',
          color: 'white',
        }}
      >
        <Typography variant="h6">No study specified.</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        overflow: 'hidden',
        backgroundColor: '#1a1a2e',
        position: 'relative',
      }}
    >
      {!iframeLoaded && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
          }}
        >
          <CircularProgress size={48} />
        </Box>
      )}
      <iframe
        ref={iframeRef}
        src={viewerUrl}
        title={`Viewer: ${studyUID}`}
        onLoad={() => setIframeLoaded(true)}
        // @ts-expect-error credentialless is valid but not yet in React types
        credentialless="true"
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
          display: 'block',
        }}
      />
    </Box>
  );
}
